import type { FC } from 'react';

import { AppPlaceholder, hasApplicationPlaceholder, Placeholder, useCms } from '@eneco/cms';
import logger from '@eneco/common/log';
import { Stack } from '@eneco/sparky';

/**
 * The page's main slot, per provider. Both CMSs answer through this one layout so a side-by-side
 * `?_cms=sitecore` / `?_cms=sanity` comparison differs only in content, never in render path:
 *
 * | slot          | provider                                                     |
 * | ------------- | ------------------------------------------------------------ |
 * | `jss-main`    | Sitecore layout service (the JSS root placeholder)           |
 * | `sections`    | Sanity, migration bundle, only when no block carries `area` (see `adapter.ts#buildTree`'s fallback) |
 * | `pageBuilder` | Sanity, greenfield POC schema                                |
 * | `main`        | the playbook's content-modelling convention                  |
 *
 * First match on the actual `CmsPage` wins, so no per-provider branching leaks into the component.
 */
const MAIN_SLOT_CANDIDATES = ['jss-main', 'sections', 'pageBuilder', 'main'] as const;

/**
 * Header/footer chrome slots. Confirmed present on real migrated content as page-root blocks
 * (`MainNavigation`/`Footer`, both structural wrappers with no fields of their own — see
 * `MainNavigation.tsx`/`Footer.tsx`), grouped under their own `area` exactly like the main slot above.
 * Absent (not just empty) on a page that doesn't carry chrome at all — rendered conditionally, same as
 * the main slot's own missing-candidate handling, just without the warning (chrome genuinely is optional
 * per page/provider, unlike the main content slot).
 */
const HEADER_SLOT_CANDIDATES = ['jss-navigationbar', 'header'] as const;
const FOOTER_SLOT_CANDIDATES = ['jss-footer', 'footer'] as const;

/**
 * CMS-agnostic layout for a page rendered through the `cmsComponents` registry (Phase E).
 */
const CmsLayout: FC = () => {
  const { page } = useCms();
  const slots = page.root.placeholders ?? {};
  // Presence, not non-empty: a page can legitimately have zero blocks in its main slot, and
  // `<Placeholder>` already no-ops on an empty array — that's a valid render, not a missing slot.
  // Presence only: `AppPlaceholder` resolves the slot name itself, so nothing here needs the match.
  const hasAppSlot = hasApplicationPlaceholder(slots);
  const mainSlot = MAIN_SLOT_CANDIDATES.find(candidate => candidate in slots);
  const headerSlot = HEADER_SLOT_CANDIDATES.find(candidate => candidate in slots);
  const footerSlot = FOOTER_SLOT_CANDIDATES.find(candidate => candidate in slots);

  // An application page (a flow) mounts one component named after its `jss-app-*` slot instead of
  // rendering a main slot, and it has no main slot to render: every block on it sits inside that
  // one area. Mirrors `AppLayout` on the Sitecore lane, including the absence of a header, because
  // an application brings its own chrome (flows render their own `NavigationBar`). Checked before
  // the main slot for the same reason `libs/layouts/src/main/index.tsx` short-circuits on it: a page
  // carrying both is an application page that also happens to hold loose blocks.
  if (hasAppSlot) {
    return (
      <Stack gap="0">
        <AppPlaceholder />
        {footerSlot && <Placeholder name={footerSlot} />}
      </Stack>
    );
  }

  if (!mainSlot) {
    // Not an error state worth blanking the page for — the page rendered, it just has no slot this
    // layout knows. Naming both sides is what makes the mismatch actionable.
    logger.warn('cMsLy1', 'No known main slot on the CMS page root', {
      componentName: page.root.componentName,
      available: Object.keys(slots).join(', '),
      expected: MAIN_SLOT_CANDIDATES.join(', '),
    });
    return null;
  }

  return (
    <Stack gap="0">
      {headerSlot && <Placeholder name={headerSlot} />}
      <Placeholder name={mainSlot} />
      {footerSlot && <Placeholder name={footerSlot} />}
    </Stack>
  );
};

export default CmsLayout;
